"use client";

import Link from "next/link";
import { ArrowRight, BookOpen, Code2, Shield, Play, Mail, ChevronRight, BookText } from "lucide-react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  AreaChart, Area, XAxis, YAxis,
} from "recharts";
import { Container, Section } from "@/components/ui/container";
import { SectionHeading } from "@/components/ui/section-heading";
import { Reveal } from "@/components/ui/reveal";
import { Counter } from "@/components/ui/counter";
import {
  HubHero, HubFeatures, HubUseCases, HubWhy, HubFAQ, HubCTA,
  CategoryHubComposer,
} from "@/components/ui/category-hub";
import {
  resourcesStats, resourcesList, resourcesFeatures, resourcesUseCases,
  resourcesWhy, resourcesFaq, resourcesCta,
} from "@/lib/data/resources-hub";

const CATEGORY_LABELS: Record<string, { label: string; icon: React.ReactNode; accent: string }> = {
  learning: { label: "Learning", icon: <BookOpen className="h-3.5 w-3.5" />, accent: "var(--color-crimson)" },
  reference: { label: "Reference", icon: <BookText className="h-3.5 w-3.5" />, accent: "var(--color-purple)" },
  compliance: { label: "Compliance", icon: <Shield className="h-3.5 w-3.5" />, accent: "var(--color-emerald)" },
  engagement: { label: "Engagement", icon: <Play className="h-3.5 w-3.5" />, accent: "var(--color-cyan)" },
};

const TYPE_LABELS: Record<string, string> = {
  guide: "Guide",
  doc: "Doc",
  paper: "Paper",
  media: "Media",
  live: "Live",
};

/* ============================================================
   BESPOKE 1 — Library signature diagram
   A stylized "knowledge base" with concentric rings of resources
   and featured documents floating around the central node.
   ============================================================ */
function LibrarySignature() {
  const ROUND = (n: number) => Math.round(n * 100) / 100;
  return (
    <div className="relative w-full max-w-[520px] aspect-square mx-auto">
      <svg viewBox="0 0 520 520" className="w-full h-full" aria-hidden>
        <defs>
          <radialGradient id="libCore" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#10b981" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx="260" cy="260" r="210" fill="url(#libCore)" />
        {[90, 140, 190, 235].map((r, i) => (
          <circle key={i} cx="260" cy="260" r={r} fill="none" stroke="rgb(255 255 255 / 0.05)" strokeDasharray={i % 2 === 0 ? "1 3" : "2 6"} />
        ))}
        {/* 17 resource nodes spread around */}
        {resourcesList.map((r, i) => {
          const angle = (i / 17) * Math.PI * 2 - Math.PI / 2;
          const ringIdx = i % 4;
          const radii = [95, 145, 195, 235];
          const radius = radii[ringIdx] ?? 235;
          const x = ROUND(260 + Math.cos(angle) * radius);
          const y = ROUND(260 + Math.sin(angle) * radius);
          return (
            <g key={r.code}>
              <line x1="260" y1="260" x2={x} y2={y} stroke={`var(--color-${r.accent})`} strokeOpacity="0.15" strokeWidth="0.4" strokeDasharray="1 3" />
              <circle cx={x} cy={y} r="13" fill={`color-mix(in oklch, var(--color-${r.accent}) 25%, transparent)`} stroke={`var(--color-${r.accent})`} strokeWidth="0.5" />
              <text x={x} y={y + 2.5} textAnchor="middle" fontSize="5.5" fontWeight="800" fill="white" letterSpacing="0.2">
                {r.code}
              </text>
            </g>
          );
        })}
        {/* Center */}
        <g transform="translate(260, 260)">
          <circle r="55" fill="rgb(10 10 18 / 0.92)" />
          <circle r="48" fill="none" stroke="rgb(16 185 129 / 0.5)" strokeWidth="1" />
          <text x="0" y="-2" textAnchor="middle" fontSize="22" fontWeight="900" fill="white" letterSpacing="-0.5">
            17
          </text>
          <text x="0" y="14" textAnchor="middle" fontSize="6" fontWeight="700" fill="#10b981" letterSpacing="2">
            RESOURCES
          </text>
        </g>
      </svg>
      <div className="absolute top-2 left-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "0.5s" }}>
        <BookOpen className="h-3.5 w-3.5" style={{ color: "var(--color-emerald)" }} />
        <span className="text-xs font-semibold">Knowledge Base</span>
      </div>
      <div className="absolute bottom-2 right-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "1.5s" }}>
        <Code2 className="h-3.5 w-3.5" style={{ color: "var(--color-cyan)" }} />
        <span className="text-xs font-semibold">API + Docs</span>
      </div>
    </div>
  );
}

/* ============================================================
   BESPOKE 2 — Resource category pie chart
   ============================================================ */
function ResourceCategoryPie() {
  const categoryCounts: Record<string, { name: string; count: number; accent: string }> = {
    learning: { name: "Learning", count: 0, accent: "var(--color-crimson)" },
    reference: { name: "Reference", count: 0, accent: "var(--color-purple)" },
    compliance: { name: "Compliance", count: 0, accent: "var(--color-emerald)" },
    engagement: { name: "Engagement", count: 0, accent: "var(--color-cyan)" },
  };
  for (const r of resourcesList) {
    const cat = categoryCounts[r.category];
    if (cat) cat.count++;
  }
  const data = Object.values(categoryCounts);
  return (
    <div className="w-full h-[280px] flex items-center">
      <ResponsiveContainer width="50%" height="100%">
        <PieChart>
          <Pie
            data={data}
            dataKey="count"
            cx="50%"
            cy="50%"
            innerRadius={50}
            outerRadius={95}
            paddingAngle={3}
            strokeWidth={0}
          >
            {data.map((entry, i) => (
              <Cell key={`cell-${i}`} fill={entry.accent} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              background: "rgb(10 10 18 / 0.95)",
              border: "1px solid rgb(255 255 255 / 0.1)",
              borderRadius: "8px",
              fontSize: "12px",
            }}
            labelStyle={{ color: "white", fontWeight: 700 }}
            itemStyle={{ color: "rgb(255 255 255 / 0.7)" }}
          />
        </PieChart>
      </ResponsiveContainer>
      <div className="w-1/2 space-y-3 pr-4">
        {data.map((d) => (
          <div key={d.name} className="flex items-center gap-3">
            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: d.accent }} />
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold">{d.name}</span>
                <span className="text-xs font-bold font-mono-num" style={{ color: d.accent }}>
                  {d.count}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   BESPOKE 3 — Resource content volume area chart
   ============================================================ */
function ResourceVolumeChart() {
  // Simulated cumulative volume over months
  const data = [
    { month: "Jan", docs: 220, videos: 18, papers: 60 },
    { month: "Feb", docs: 232, videos: 22, papers: 64 },
    { month: "Mar", docs: 245, videos: 28, papers: 68 },
    { month: "Apr", docs: 258, videos: 32, papers: 72 },
    { month: "May", docs: 275, videos: 38, papers: 80 },
    { month: "Jun", docs: 290, videos: 44, papers: 88 },
    { month: "Jul", docs: 305, videos: 50, papers: 96 },
    { month: "Aug", docs: 318, videos: 56, papers: 104 },
  ];
  return (
    <div className="w-full h-[280px]">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
          <defs>
            <linearGradient id="gDocs" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#dc143c" stopOpacity={0.4} />
              <stop offset="100%" stopColor="#dc143c" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gPapers" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.4} />
              <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gVideos" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#06b6d4" stopOpacity={0.4} />
              <stop offset="100%" stopColor="#06b6d4" stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis dataKey="month" tick={{ fontSize: 10, fill: "rgb(255 255 255 / 0.6)" }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 10, fill: "rgb(255 255 255 / 0.5)" }} axisLine={false} tickLine={false} width={30} />
          <Tooltip
            contentStyle={{
              background: "rgb(10 10 18 / 0.95)",
              border: "1px solid rgb(255 255 255 / 0.1)",
              borderRadius: "8px",
              fontSize: "12px",
            }}
            labelStyle={{ color: "white", fontWeight: 700 }}
            itemStyle={{ color: "rgb(255 255 255 / 0.7)" }}
          />
          <Area type="monotone" dataKey="docs" stroke="var(--color-crimson)" strokeWidth={2} fill="url(#gDocs)" name="Docs" />
          <Area type="monotone" dataKey="papers" stroke="var(--color-purple)" strokeWidth={2} fill="url(#gPapers)" name="Papers" />
          <Area type="monotone" dataKey="videos" stroke="var(--color-cyan)" strokeWidth={2} fill="url(#gVideos)" name="Videos" />
        </AreaChart>
      </ResponsiveContainer>
      <div className="flex justify-center gap-4 mt-3">
        {[
          { label: "Documentation", color: "var(--color-crimson)" },
          { label: "Papers", color: "var(--color-purple)" },
          { label: "Videos", color: "var(--color-cyan)" },
        ].map((l) => (
          <div key={l.label} className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full" style={{ background: l.color }} />
            <span className="text-[10px] font-semibold text-[rgb(var(--fg-muted))] uppercase tracking-wider">{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   BESPOKE 4 — Resource bento grid (grouped by category)
   ============================================================ */
function ResourceBento() {
  const categories = ["learning", "reference", "compliance", "engagement"] as const;
  return (
    <div className="space-y-10">
      {categories.map((cat) => {
        const items = resourcesList.filter((r) => r.category === cat);
        const catLabel = CATEGORY_LABELS[cat];
        return (
          <Reveal key={cat}>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-xs font-bold uppercase tracking-wider"
                  style={{ color: catLabel?.accent }}
                >
                  {catLabel?.icon}
                  {catLabel?.label}
                </span>
                <div className="text-xs text-[rgb(var(--fg-subtle))] font-semibold">
                  {items.length} {items.length === 1 ? "resource" : "resources"}
                </div>
                <div className="flex-1 h-px bg-gradient-to-r from-[rgb(var(--border)/0.2)] to-transparent" />
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {items.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    className="group block glass rounded-2xl p-5 h-full hover:scale-[1.02] transition-transform"
                    style={{
                      boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${item.accent}) 12%, transparent)`,
                    }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span
                        className="grid place-items-center h-9 w-9 rounded-lg"
                        style={{
                          background: `color-mix(in oklch, var(--color-${item.accent}) 18%, transparent)`,
                          boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${item.accent}) 35%, transparent)`,
                        }}
                      >
                        <span className="text-[10px] font-bold tracking-wider" style={{ color: `var(--color-${item.accent})` }}>
                          {item.code}
                        </span>
                      </span>
                      <span
                        className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider"
                        style={{
                          background: `color-mix(in oklch, var(--color-${item.accent}) 12%, transparent)`,
                          color: `var(--color-${item.accent})`,
                        }}
                      >
                        {TYPE_LABELS[item.type]}
                      </span>
                    </div>
                    <h3 className="text-sm font-bold">{item.title}</h3>
                    <p className="mt-1.5 text-xs text-[rgb(var(--fg-muted))] leading-relaxed line-clamp-2">
                      {item.description}
                    </p>
                    <div className="mt-3 flex items-center gap-1 text-xs font-semibold opacity-70 group-hover:opacity-100" style={{ color: `var(--color-${item.accent})` }}>
                      Open <ChevronRight className="h-3 w-3" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </Reveal>
        );
      })}
    </div>
  );
}

/* ============================================================
   PAGE COMPOSER
   ============================================================ */
export function ResourcesHubPage() {
  return (
    <CategoryHubComposer
      overviewWrap="crimson"
      featuresWrap="purple"
      useCasesWrap="emerald"
      whyWrap="cyan"
      faqWrap="amber"
      ctaWrap="violet"
      hero={
        <section id="top" className="relative overflow-hidden pt-8 pb-16 sm:pt-12 sm:pb-24">
          <div className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute top-10 left-1/4 h-72 w-72 rounded-full bg-[var(--color-emerald)] opacity-22 blur-[130px] animate-pulse-glow" />
            <div className="absolute top-32 right-1/4 h-80 w-80 rounded-full bg-[var(--color-purple)] opacity-18 blur-[140px] animate-pulse-glow" style={{ animationDelay: "2s" }} />
            <div className="absolute bottom-10 left-1/3 h-72 w-72 rounded-full bg-[var(--color-cyan)] opacity-15 blur-[140px] animate-pulse-glow" style={{ animationDelay: "4s" }} />
          </div>
          <Container size="wide">
            <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-6 items-center">
              <div className="flex flex-col items-start gap-6">
                <Reveal>
                  <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full glass">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--color-emerald)] opacity-75 animate-ping" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--color-emerald)]" />
                    </span>
                    <span className="eyebrow-tiny uppercase tracking-[0.28em]">Perception X2 Knowledge Base</span>
                  </div>
                </Reveal>
                <Reveal delay={80}>
                  <h1 className="display-hero text-balance">
                    The Resource Library{" "}
                    <span className="bg-gradient-to-br from-[var(--color-emerald)] via-[var(--color-cyan)] to-[var(--color-purple)] bg-clip-text text-transparent">
                      Behind the Platform
                    </span>
                  </h1>
                </Reveal>
                <Reveal delay={160}>
                  <p className="text-[1.35rem] sm:text-[1.5rem] leading-[1.45] font-medium text-[rgb(var(--fg))] max-w-2xl">
                    17 resource verticals. 4 content categories. 5 formats. Documentation, guides, whitepapers, case studies, technical papers, webinars, videos, and the full AI ethics framework.
                  </p>
                </Reveal>
                <Reveal delay={220}>
                  <p className="text-body leading-relaxed text-[rgb(var(--fg-muted))] max-w-2xl">
                    Every resource is practitioner-authored, version-synchronized with the platform, and continuously updated. Whether you are an engineer integrating the platform, a strategist briefing executives, or a compliance professional evaluating posture — the library has you covered.
                  </p>
                </Reveal>
                <Reveal delay={280}>
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <Link
                      href="/resources/documentation"
                      className="btn-primary-gradient h-14 px-8 inline-flex items-center gap-2 rounded-full font-semibold text-base shadow-2xl shadow-[var(--color-emerald)/30]"
                    >
                      Browse Documentation <ArrowRight className="h-4 w-4" />
                    </Link>
                    <Link
                      href="/resources/whitepapers"
                      className="btn-glass h-14 px-8 inline-flex items-center gap-2 rounded-full font-semibold text-base"
                    >
                      Whitepapers
                    </Link>
                  </div>
                </Reveal>
                <Reveal delay={360}>
                  <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 max-w-3xl w-full">
                    {resourcesStats.map((s) => (
                      <div
                        key={s.label}
                        className="glass rounded-2xl p-3 card-glow"
                        style={{ ["--glow" as string]: `var(--color-${s.accent})` }}
                      >
                        <div className="display-stat-sm" style={{ color: `var(--color-${s.accent})` }}>
                          <Counter to={s.to} suffix={s.suffix ?? ""} decimals={s.decimals ?? 0} />
                        </div>
                        <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))] mt-1.5 font-semibold uppercase tracking-wider">
                          {s.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </Reveal>
              </div>
              <Reveal delay={200} className="order-first lg:order-last">
                <LibrarySignature />
              </Reveal>
            </div>
          </Container>
        </section>
      }
      overview={
        <Section id="library" className="relative">
          <Container>
            <SectionHeading
              eyebrow="Resource Library"
              title="Every Resource. Every Category. Every Format."
              description="Browse the 17 resources organized by category — learning, reference, compliance, and engagement."
              accent="crimson"
            />
            <ResourceBento />
          </Container>
        </Section>
      }
      features={
        <>
          <Section id="distribution" className="relative">
            <Container>
              <div className="grid lg:grid-cols-2 gap-8">
                <div className="glass rounded-2xl p-6">
                  <SectionHeading
                    eyebrow="Category Distribution"
                    title="How the Library Breaks Down"
                    description="Four content categories — Learning, Reference, Compliance, Engagement — cover the full resource surface."
                    accent="purple"
                    align="left"
                    className="mb-6"
                  />
                  <ResourceCategoryPie />
                </div>
                <div className="glass rounded-2xl p-6">
                  <SectionHeading
                    eyebrow="Content Volume"
                    title="Library Growth Over Time"
                    description="Cumulative content volume — documentation, papers, and videos — across the past 8 months."
                    accent="cyan"
                    align="left"
                    className="mb-6"
                  />
                  <ResourceVolumeChart />
                </div>
              </div>
            </Container>
          </Section>
          <Section id="features" className="relative mt-8">
            <Container>
              <HubFeatures
                eyebrow="Resource Properties"
                title="Properties That Define the Library"
                description="Eight properties shared by every Perception X2 resource — comprehensive coverage, practitioner authorship, version synchronization, and more."
                features={resourcesFeatures}
                accent="purple"
              />
            </Container>
          </Section>
        </>
      }
      useCases={
        <HubUseCases
          eyebrow="Resource Applications"
          title="Six Common Use Patterns"
          description="How different audiences leverage the Perception X2 resource library — from engineering onboarding to compliance evaluation."
          cases={resourcesUseCases}
          accent="emerald"
        />
      }
      why={
        <HubWhy
          eyebrow="Why This Library"
          title="The Differentiators That Matter"
          items={resourcesWhy}
          accent="cyan"
        />
      }
      faq={
        <HubFAQ
          eyebrow="Resources FAQ"
          title="Frequently Asked Questions"
          items={resourcesFaq}
          accent="amber"
        />
      }
      cta={
        <HubCTA
          eyebrow={resourcesCta.eyebrow}
          title={resourcesCta.title}
          description={resourcesCta.description}
          primary={resourcesCta.primary}
          secondary={resourcesCta.secondary}
          trustItems={resourcesCta.trustItems}
          accent="violet"
        />
      }
    />
  );
}
